import { TextField, Typography } from '@mui/material';
import { connect, getIn } from "formik";
import React from 'react';

// Utility function to sanitize input and prevent XSS
export const sanitizeInput = (value, fieldType) => {
    if (typeof value !== 'string') return value;

    // Define dangerous characters that need to be escaped/blocked
    // const dangerousChars = {
    //     '<': '&lt;',
    //     '>': '&gt;',
    //     '"': '&quot;',
    //     "'": '&#x27;',
    //     '/': '&#x2F;',
    //     '\\': '&#x5C;',
    //     '&': '&amp;',
    //     '`': '&#96;',
    //     '=': '&#x3D;'
    // };

    if (fieldType === 'json') {
        return value.replace(/[<>`']/g, '')
    }
    if (fieldType === 'url') {
        return value.replace(/[<>"'`]/g, '');
    }
    // Remove dangerous characters completely
    return value.replace(/[<>"'\/\\&`=]/g, '');
};

// Additional validation for script-related patterns
export const containsScriptPatterns = (value, fieldType) => {
    if (fieldType === 'url') {
        return false;
    }
    if (typeof value !== 'string') return false;

    const scriptPatterns = [
        /<script[^>]*>.*?<\/script>/gi,
        /javascript:/gi,
        /on\w+\s*=/gi,
        /eval\s*\(/gi,
        /expression\s*\(/gi,
        /vbscript:/gi,
        /data:text\/html/gi,
        /&#x[0-9a-f]+;?/gi,
        /\\x[0-9a-f]+/gi,
    ];
    scriptPatterns.push(/\%[0-9a-f]+/gi);
    return scriptPatterns.some(pattern => pattern.test(value));
};


const OZInput = connect(({
    type = "text",
    name,
    label,
    height,
    disabled,
    formik,
    fieldType,
    disableHelperText = false,
    allowUnsafeInput = false,
    ...additionalProps
}) => {

    const error = getIn(formik.errors, name);
    const touch = getIn(formik.touched, name);
    const rawValue = getIn(formik.values, name);

    // Custom change handler with sanitization
    const handleChange = (event) => {
        let { value } = event.target;

        // Skip sanitization for password fields or if explicitly allowed
        if (type === 'password' || allowUnsafeInput) {
            // Still check for obvious script patterns even in password fields
            if (containsScriptPatterns(value, fieldType)) {
                console.warn('Potentially dangerous input detected and blocked');
                return; // Don't update the field
            }
        } else {
            // Sanitize the input
            value = sanitizeInput(value, fieldType);

            // Additional check for script patterns
            if (containsScriptPatterns(value, fieldType)) {
                console.warn('Script patterns detected and blocked');
                return; // Don't update the field
            }
        }

        // Update the event with sanitized value
        event.target.value = value;
        formik.handleChange(event);
    };

    // Custom blur handler
    const handleBlur = (event) => {
        // Perform final validation on blur
        const { value } = event.target;

        if (!allowUnsafeInput && type !== 'password' && containsScriptPatterns(value, fieldType)) {
            // Clear the field if dangerous patterns are found
            formik.setFieldValue(name, '');
            formik.setFieldError(name, 'Invalid characters detected. Please remove any script-like content.');
        }

        formik.handleBlur(event);
    };

    return (
        <React.Fragment>
            {label && (
                <Typography
                    htmlFor={name}
                    fontSize='12px'
                    fontWeight={400}
                    lineHeight='14px'
                    marginBottom={'6px'}
                    marginLeft='1px'
                    color={disabled ? '#99A0A8' : (name === "authConfig" || name === "federationUrl" || name === "folderName" || name === "bucketName" || name === "accessKey" || name === "secretKey" || name === "gcptype" || name === "projectId" || name === "PrivateId" || name === "PrivateKey" || name === "clientEmail" || name === "clientId" || name === "authUrl" || name === "tokenUrl" || name === "auth_provider_x509_curt_url" || name === "client_provider_x509_curt_url" || name === "url" || name === "port" || name === "userDn" || name === "domain" || name === "dc") ? '#47586E' : '#212121'}
                >
                    {label}
                </Typography>
            )}
            <TextField
                sx={{
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        backgroundColor: '#ffff',
                        height: height || '44px',
                        fontWeight: (name === "authConfig" || name === "federationUrl" || name === "folderName" || name === "bucketName" || name === "accessKey" || name === "secretKey" || name === "gcptype" || name === "projectId" || name === "PrivateId" || name === "PrivateKey" || name === "clientEmail" || name === "clientId" || name === "authUrl" || name === "tokenUrl" || name === "auth_provider_x509_curt_url" || name === "client_provider_x509_curt_url" || name === "url" || name === "port" || name === "userDn" || name === "domain" || name === "dc") && 400,
                        fontSize: (name === "authConfig" || name === "federationUrl" || name === "folderName" || name === "bucketName" || name === "accessKey" || name === "secretKey" || name === "gcptype" || name === "projectId" || name === "PrivateId" || name === "PrivateKey" || name === "clientEmail" || name === "clientId" || name === "authUrl" || name === "tokenUrl" || name === "auth_provider_x509_curt_url" || name === "client_provider_x509_curt_url" || name === "url" || name === "port" || name === "userDn" || name === "domain" || name === "dc") ? "16px" : "14px"
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D6D6D7'
                    },
                    '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #D6D6D7'
                    }
                }}
                fullWidth
                disabled={disabled}
                type={type}
                autoComplete={type === "password" ? "new-password" : "off"}
                onChange={handleChange}
                onBlur={handleBlur}
                value={rawValue}
                error={touch && Boolean(error)}
                helperText={disableHelperText ? '' : (touch && error)}
                name={name}
                id={name}
                inputProps={{
                    // Additional security attributes
                    'data-sanitized': !allowUnsafeInput,
                    ...(additionalProps.inputProps || {})
                }}
                {...additionalProps}
            />
        </React.Fragment>
    );
});

export default OZInput;